import type { TabId } from 'components/mui/common/Tabs';

export const TAB_QUERY_PARAM = 'tab';

const STORAGE_PREFIX = 'flow:record-position';

export interface RecordTabPosition {
  scrollTop: number;
  sectionStates: Record<string, boolean>;
}

export interface RecordPosition {
  activeTab: string | null;
  tabs: Record<string, RecordTabPosition>;
}

const createEmptyPosition = (): RecordPosition => ({ activeTab: null, tabs: {} });

const getStorageKey = (agendaId: string, recordId: string): string => `${STORAGE_PREFIX}:${agendaId}:${recordId}`;

export const getTabKey = (tab: TabId | undefined): string => (tab === null || tab === undefined ? '' : String(tab));

export const parseTabId = (value: string | null | undefined): TabId => {
  if (value === null || value === undefined || value === '') return null;

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const getSessionStorage = (): Storage | null => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
};

const parseTabPosition = (value: unknown): RecordTabPosition | null => {
  if (value === null || typeof value !== 'object') return null;

  const candidate = value as Partial<RecordTabPosition>;
  const sectionStates: Record<string, boolean> = {};

  if (candidate.sectionStates !== null && typeof candidate.sectionStates === 'object') {
    Object.entries(candidate.sectionStates).forEach(([sectionId, expanded]) => {
      if (typeof expanded === 'boolean') {
        sectionStates[sectionId] = expanded;
      }
    });
  }

  return {
    scrollTop: typeof candidate.scrollTop === 'number' && Number.isFinite(candidate.scrollTop) ? candidate.scrollTop : 0,
    sectionStates,
  };
};

const parsePosition = (raw: string | null): RecordPosition => {
  if (!raw) return createEmptyPosition();

  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return createEmptyPosition();

    const candidate = parsed as Partial<RecordPosition>;
    const tabs: Record<string, RecordTabPosition> = {};

    if (candidate.tabs !== null && typeof candidate.tabs === 'object') {
      Object.entries(candidate.tabs).forEach(([tabKey, tabPosition]) => {
        const parsedTabPosition = parseTabPosition(tabPosition);

        if (parsedTabPosition) {
          tabs[tabKey] = parsedTabPosition;
        }
      });
    }

    return {
      activeTab: typeof candidate.activeTab === 'string' ? candidate.activeTab : null,
      tabs,
    };
  } catch {
    return createEmptyPosition();
  }
};

export const readRecordPosition = (agendaId: string, recordId: string): RecordPosition => {
  const storage = getSessionStorage();

  if (!storage || !agendaId || !recordId) return createEmptyPosition();

  return parsePosition(storage.getItem(getStorageKey(agendaId, recordId)));
};

export const updateRecordPosition = (
  agendaId: string,
  recordId: string,
  updater: (current: RecordPosition) => RecordPosition
): RecordPosition | null => {
  const storage = getSessionStorage();

  if (!storage || !agendaId || !recordId) return null;

  const storageKey = getStorageKey(agendaId, recordId);
  const current = parsePosition(storage.getItem(storageKey));
  const next = updater(current);

  if (next === current) return next;

  try {
    storage.setItem(storageKey, JSON.stringify(next));
  } catch {
    // Ignore storage quota / privacy mode errors, the position is only a convenience.
  }

  return next;
};

export const getStoredActiveTab = (agendaId: string, recordId: string): TabId => {
  const { activeTab } = readRecordPosition(agendaId, recordId);

  return activeTab === null ? null : parseTabId(activeTab);
};
